import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
  useEffect,
} from "react";
import Student from "types/Student";
import fetchWithCredentials from "apiClient";

interface StudentsContextType {
  students: Student[];
  pendingStudents: Student[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  activeStudentsCount: number;
  selectedStudentId: number | null;
  setSelectedStudentId: (studentId: number | null) => void;
}

const StudentsContext = createContext<StudentsContextType | undefined>(
  undefined
);

export function StudentsProvider({ children }: { children: ReactNode }) {
  const [students, setStudents] = useState<Student[]>([]);
  const [pendingStudents, setPendingStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(
    null
  );

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [regularStudents, pendingStudentsData] = await Promise.all([
        fetchWithCredentials(`/api/teacher/students/`),
        fetchWithCredentials(`/api/teacher/pending-students/`),
      ]);
      setStudents(regularStudents);
      setPendingStudents(pendingStudentsData);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Error fetching students");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const refresh = useCallback(async () => {
    await fetchStudents();
  }, [fetchStudents]);

  const activeStudentsCount = useMemo(
    () => students.filter((student) => student.status === "active").length,
    [students]
  );

  return (
    <StudentsContext.Provider
      value={{
        students,
        pendingStudents,
        loading,
        error,
        refresh,
        activeStudentsCount,
        selectedStudentId,
        setSelectedStudentId,
      }}
    >
      {children}
    </StudentsContext.Provider>
  );
}

export function useStudents() {
  const context = useContext(StudentsContext);
  if (!context) {
    throw new Error("useStudents must be used within a StudentsProvider");
  }
  return context;
}
